import React, { useReducer, useCallback } from "react";
import PropTypes from "prop-types";
import Autosuggest from "react-autosuggest";
import Paper from "@material-ui/core/Paper";
import withStyles from "components/with-styles";
import { renderSuggestion, getSuggestions } from "./parts/autocomplete-suggestion";
import renderInputComponent from "./parts/autocomplete-input";
import styles from "./styles/autocomplete.css.js";

const initialState = { value: "", currentSuggestions: [] };

const reducerTypes = {
  FETCH: "FETCH",
  CLEAR: "CLEAR",
  CHANGE: "CHANGE",
};

const reducer = (suggestions, getSuggestionValue) => (state, action) => {
  switch (action.type) {
    case reducerTypes.FETCH:
      return {
        ...state,
        currentSuggestions: getSuggestions(state.value, suggestions, getSuggestionValue),
      };
    case reducerTypes.CLEAR:
      return {
        ...state,
        currentSuggestions: [],
      };
    case reducerTypes.CHANGE:
      return {
        ...state,
        value: action.value,
      };
    default:
      throw new Error();
  }
};

const renderSuggestionsContainer = options => (
  <Paper elevation={2} {...options.containerProps} square>
    {options.children}
  </Paper>
);

const Autocomplete = ({ className, classes, getSuggestionValue, suggestions, placeholder, label, onChange, name }) => {
  const [state, dispatch] = useReducer(reducer(suggestions, getSuggestionValue), initialState);

  const handleChange = useCallback(
    (event, { newValue }) => {
      dispatch({ type: reducerTypes.CHANGE, value: newValue });
      onChange(newValue);
    },
    [onChange],
  );

  const handleFetch = useCallback(() => dispatch({ type: reducerTypes.FETCH }), []);
  const handleClear = useCallback(() => dispatch({ type: reducerTypes.CLEAR }), []);

  return (
    <div className={className}>
      <Autosuggest
        renderInputComponent={renderInputComponent}
        suggestions={state.currentSuggestions}
        onSuggestionsFetchRequested={handleFetch}
        onSuggestionsClearRequested={handleClear}
        getSuggestionValue={getSuggestionValue}
        renderSuggestion={renderSuggestion(getSuggestionValue)}
        inputProps={{
          placeholder,
          value: state.value,
          onChange: handleChange,
          label,
          name,
          InputLabelProps: {
            shrink: true,
          },
        }}
        theme={{
          container: classes.autocomplete_container,
          suggestionsContainerOpen: classes.autocomplete_suggestionscontainer__open,
          suggestionsList: classes.autocomplete_suggestionslist,
          suggestion: classes.autocomplete_suggestion,
        }}
        renderSuggestionsContainer={renderSuggestionsContainer}
      />
    </div>
  );
};

Autocomplete.propTypes = {
  /**
   * Nombre del input
   */
  name: PropTypes.string,
  /**
   * Clase adicional del componente.
   */
  className: PropTypes.string,
  /**
   * Función que obtiene el texto a mostrar en el autocomplete a partir del elemento.
   **/
  getSuggestionValue: PropTypes.func.isRequired,
  /**
   * Lista de sugerencias del autocomplete.
   **/
  suggestions: PropTypes.array.isRequired,
  /**
   * Placeholder del input del autocomplete.
   **/
  placeholder: PropTypes.string,
  /**
   * Label del input del autocomplete.
   **/
  label: PropTypes.string,
  /**
   * Callback que se llama cuando cambia el valor del input.
   **/
  onChange: PropTypes.func,
};

export default withStyles(styles)(Autocomplete);
